import React, { useState, useEffect, useCallback } from 'react';
import {
  FaFilter, FaDownload, FaFileExcel, FaFileCsv, FaSearch, FaUsers,
  FaCheckCircle, FaTimesCircle, FaClock, FaHourglassHalf, FaRedo,
} from 'react-icons/fa';
import api, { downloadExport, errorMessage } from '../api';
import {
  PageShell, Card, StatCard, Button, Field, inputClass, Alert, Loading,
  RateBar, Table, MONTHS,
} from './attendance/ui';

const ATTENDANCE_STATUSES = [
  ['', 'Any status'], ['present', 'Present'], ['late', 'Late'],
  ['half_day', 'Half day'], ['absent', 'Absent'], ['on_leave', 'On leave'],
];

const isoWeek = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
};

/**
 * Weekly / monthly / custom-range reporting with the filters and exports the
 * proposal calls for: staff, department, location, date range, status.
 */
const ReportsAnalytics = () => {
  const now = new Date();
  const [period, setPeriod] = useState('monthly');
  const [filters, setFilters] = useState({
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    week: isoWeek(now),
    start_date: '',
    end_date: '',
    department: '',
    location: '',
    employee: '',
    status: '',
  });
  const [options, setOptions] = useState({ departments: [], locations: [], staff: [] });
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('departments/'),
      api.get('locations/'),
      api.get('employees/directory/'),
    ]).then(([departments, locations, staff]) => {
      setOptions({
        departments: departments.data,
        locations: locations.data,
        staff: staff.data,
      });
    }).catch(() => { /* filters simply stay empty */ });
  }, []);

  /** Only the parameters that matter for the selected period. */
  const queryParams = useCallback(() => {
    const base = {
      period,
      department: filters.department || undefined,
      location: filters.location || undefined,
      employee: filters.employee || undefined,
      status: filters.status || undefined,
    };
    if (period === 'custom') {
      return { ...base, start_date: filters.start_date || undefined, end_date: filters.end_date || undefined };
    }
    if (period === 'weekly') {
      return { ...base, week: filters.week, year: filters.year };
    }
    return { ...base, month: filters.month, year: filters.year };
  }, [period, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('reports/', { params: queryParams() });
      setReport(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not build the report.'));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const runExport = async (format, type) => {
    setExporting(`${type}-${format}`);
    setError('');
    try {
      const extension = format === 'xlsx' ? 'xlsx' : 'csv';
      await downloadExport(
        { ...queryParams(), format, type },
        `attendance_${type}_${filters.year}.${extension}`,
      );
    } catch (err) {
      setError(errorMessage(err, 'Export failed.'));
    } finally {
      setExporting('');
    }
  };

  const update = (key) => (event) =>
    setFilters((current) => ({ ...current, [key]: event.target.value }));

  const reset = () => {
    setFilters((current) => ({
      ...current,
      department: '', location: '', employee: '', status: '',
      start_date: '', end_date: '',
    }));
    setSearch('');
  };

  const totals = report?.totals;
  const rows = (report?.rows || []).filter((row) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return `${row.employeeName} ${row.employeeId} ${row.department || ''}`
      .toLowerCase().includes(needle);
  });

  return (
    <PageShell
      title="Reports & Analytics"
      subtitle={report ? `${report.startDate} to ${report.endDate}` : 'Attendance reporting'}
      actions={
        <>
          <Button variant="secondary" disabled={!!exporting} onClick={() => runExport('csv', 'summary')}>
            <FaFileCsv /> Summary CSV
          </Button>
          <Button variant="secondary" disabled={!!exporting} onClick={() => runExport('xlsx', 'summary')}>
            <FaFileExcel /> Summary Excel
          </Button>
          <Button disabled={!!exporting} onClick={() => runExport('xlsx', 'detailed')}>
            <FaDownload /> Detailed Excel
          </Button>
        </>
      }
    >
      <Alert>{error}</Alert>

      <Card className="mb-6" title={<span className="flex items-center gap-2 dark:text-slate-100"><FaFilter className="text-gray-400 dark:text-slate-300" /> Filters</span>}
        action={<Button variant="subtle" onClick={reset}><FaRedo /> Reset</Button>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Period">
            <select value={period} onChange={(event) => setPeriod(event.target.value)} className={inputClass}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom range</option>
            </select>
          </Field>

          {period === 'monthly' && (
            <Field label="Month">
              <select value={filters.month} onChange={update('month')} className={inputClass}>
                {MONTHS.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
              </select>
            </Field>
          )}
          {period === 'weekly' && (
            <Field label="ISO week" hint="Week 1 is the first week with a Thursday in it.">
              <input type="number" min="1" max="53" value={filters.week}
                onChange={update('week')} className={inputClass} />
            </Field>
          )}
          {period === 'custom' ? (
            <>
              <Field label="From">
                <input type="date" value={filters.start_date} onChange={update('start_date')} className={inputClass} />
              </Field>
              <Field label="To">
                <input type="date" value={filters.end_date} onChange={update('end_date')} className={inputClass} />
              </Field>
            </>
          ) : (
            <Field label="Year">
              <select value={filters.year} onChange={update('year')} className={inputClass}>
                {Array.from({ length: 6 }, (_, index) => now.getFullYear() - index)
                  .map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
          )}

          <Field label="Department">
            <select value={filters.department} onChange={update('department')} className={inputClass}>
              <option value="">All departments</option>
              {options.departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
          <Field label="Location">
            <select value={filters.location} onChange={update('location')} className={inputClass}>
              <option value="">All locations</option>
              {options.locations.map((item) => (
                <option key={item.id} value={item.id}>{item.name} ({item.type})</option>
              ))}
            </select>
          </Field>
          <Field label="Staff member">
            <select value={filters.employee} onChange={update('employee')} className={inputClass}>
              <option value="">All staff</option>
              {options.staff.map((item) => (
                <option key={item.id} value={item.employeeId}>{item.name} ({item.employeeId})</option>
              ))}
            </select>
          </Field>
          <Field label="Attendance status" hint="Applies to the detailed export.">
            <select value={filters.status} onChange={update('status')} className={inputClass}>
              {ATTENDANCE_STATUSES.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      {loading ? <Loading label="Building report…" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <StatCard icon={<FaUsers />} label="Staff in report" tone="gray" value={totals?.staffCount ?? 0} />
            <StatCard icon={<FaCheckCircle />} label="Days present" tone="green" value={totals?.daysPresent ?? 0} />
            <StatCard icon={<FaTimesCircle />} label="Days absent" tone="red" value={totals?.daysAbsent ?? 0} />
            <StatCard icon={<FaClock />} label="Late days" tone="amber" value={totals?.lateDays ?? 0} />
            <StatCard icon={<FaHourglassHalf />} label="Working hours" tone="blue"
              value={totals?.totalWorkingHours ?? 0} hint={`+${totals?.overtimeHours ?? 0}h overtime`} />
            <StatCard icon={<FaCheckCircle />} label="Attendance" tone="purple"
              value={`${totals?.attendancePercentage ?? 0}%`} hint={`${totals?.leaveDays ?? 0} leave days`} />
          </div>

          <Card
            title={<span className="dark:text-slate-100">Per staff member ({rows.length})</span>}
            action={
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm dark:text-slate-400" />
                <input value={search} onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search name or ID"
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#15A857] dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder:text-slate-400" />
              </div>
            }
          >
            <Table
              columns={['Staff ID', 'Name', 'Department', 'Location', 'Working days',
                'Present', 'Absent', 'Late', 'Leave', 'Hours', 'Overtime', 'Attendance']}
              rows={rows}
              empty="No staff match these filters."
              renderRow={(row) => (
                <tr key={row.employeeId} className="border-b last:border-0 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-800/70">
                  <td className="py-3 pr-4 font-mono text-xs text-gray-600 dark:text-slate-300">{row.employeeId}</td>
                  <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap dark:text-slate-100">{row.employeeName}</td>
                  <td className="py-3 pr-4 whitespace-nowrap dark:text-slate-300">{row.department || '—'}</td>
                  <td className="py-3 pr-4 whitespace-nowrap dark:text-slate-300">{row.location || '—'}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{row.totalWorkingDays}</td>
                  <td className="py-3 pr-4 text-green-700 font-semibold dark:text-emerald-300">{row.daysPresent}</td>
                  <td className="py-3 pr-4 text-red-600 dark:text-rose-300">{row.daysAbsent}</td>
                  <td className="py-3 pr-4 text-amber-600 dark:text-amber-300">{row.lateDays}</td>
                  <td className="py-3 pr-4 text-violet-600 dark:text-violet-300">{row.leaveDays}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{row.totalWorkingHours}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{row.overtimeHours}</td>
                  <td className="py-3 pr-4 w-40"><RateBar value={row.attendancePercentage} /></td>
                </tr>
              )}
            />
          </Card>
        </>
      )}
    </PageShell>
  );
};

export default ReportsAnalytics;
